import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
  Alert,
  Spinner,
} from "react-bootstrap";
import { IconButton } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CustomDataGrid from "../components/CustomDataGrid";
import { createUser, fetchUsersFromDB } from "../utils/api";
import { validateForm,formatPhoneNumber } from "../utils/PreviewUtils";
import {
  existingUsersGridColumns,
  existingUsersRowMapping,
  generateGridRows,
  generateGridColumns,
} from "../utils/PageUtils";
import PageHelmet from "../components/PageHelmet";

function CreateNewUser({ userRole, bearerToken }) {
  const [statusMessage, setStatusMessage] = useState(null);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingUsers, setExistingUsers] = useState([]);
  const [usersLength, setUsersLength] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [refreshGrid, setRefreshGrid] = useState(true);
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+1",
  });
  const newUserFields = [
    {
      name: "name",
      label: " Your organizational name ",
      tooltip:
        "Enter the value for the name of the organization/business you wish to register with",
      placeholder: "For ex: Apple inc.",
    },
    {
      name: "email",
      label: "Your email address",
      tooltip:
        "Enter the value for the email address you wish to register with",
      placeholder: "For ex: name@example.com",
    },
  ];
  useEffect(() => {
    const fetchUserCredentials = async () => {
      setUsersLoading(true);
      setError(null);
      if (!bearerToken) {
        return;
      }
      try {
        const response = await fetchUsersFromDB(bearerToken);

        if (response.status === 200) {
          const data = response.data;
          if (data.count > 0) {
            setUsersLength(data.count);
            setExistingUsers(data.users);
          } else {
            return [];
          }
        } else if (response.status === 404) {
          setError("No user credentials were returned by the API");
          return [];
        } else if (response.status === 500) {
          return [];
        }
      } catch (error) {
        return [];
      } finally {
        setUsersLoading(false);
        if (refreshGrid) {
          setRefreshGrid(false);
        }
      }
    };
    if (refreshGrid) {
      fetchUserCredentials();
    }
  }, [refreshGrid]);

  const handleInputChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    setFormValues((prevValues) => {
      if (name === "phone") {
        const formattedPhone = formatPhoneNumber(value);
        return {
          ...prevValues,
          phone: formattedPhone,
        };
      } else {
        return {
          ...prevValues,
          [name]: value,
        };
      }
    });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMessage(null);
    setIsError(false);

    const { name, email, phone, countryCode } = formValues;
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const fullPhone = `${countryCode}${phone.replace(/\D/g, "")}`;
    const { isValid, errorMessage } = validateForm(
      trimmedName,
      fullPhone,
      trimmedEmail
    );

    if (!isValid) {
      setStatusMessage(errorMessage);
      setIsError(true);
      setLoading(false);
      return;
    }
    try {
      const response = await createUser(
        trimmedName,
        trimmedEmail,
        fullPhone,
        bearerToken
      );
      setStatusMessage(response.message);
      if (response.success) {
        setRefreshGrid(true);
        setFormValues({
          name: "",
          email: "",
          phone: "",
          countryCode: "+1",
        });
      }
      setIsError(!response.success);
    } catch (error) {
      setStatusMessage("An error occurred while creating the user.");
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const existingUsersRows = generateGridRows(
    existingUsers,
    existingUsersRowMapping
  );
  const existingUsersColumns = generateGridColumns(existingUsersGridColumns,"name");
  return userRole === "admin" ? (
    <div>
      <PageHelmet
        title="Create User"
        description="Create a user."
        url={`${process.env.REACT_APP_REDIRECT_URI}/create_user`}
      />
      <div className="user-container">
        <h2 className="create-user-heading">Create user</h2>
        <Form className="proof-form-container" onSubmit={handleSubmit}>
          {newUserFields.map((field) => (
            <Form.Group
              controlId={`form-${field.name}`}
              className="form-group-custom"
              key={field.name}
            >
              <div className="modal-form-field" key={field.name}>
                <Form.Label className="custom-label"> {field.label}</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{field.tooltip}</Tooltip>}
                >
                  <IconButton>
                    <HelpOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </OverlayTrigger>
              </div>
              <Form.Control
                type="string"
                name={field.name}
                placeholder={field.placeholder}
                onChange={handleInputChange}
                value={formValues[field.name]}
                className="custom-placeholder"
              />
            </Form.Group>
          ))}
          <Form.Group
            className="form-group-custom"
            controlId="form-countryCode-phone"
          >
            <div className="modal-form-field">
              <Form.Label className="custom-label">
                Your contact number
              </Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Enter the value for the contact number you wish to be
                    contacted with
                  </Tooltip>
                }
              >
                <IconButton>
                  <HelpOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </OverlayTrigger>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <Form.Control
                type="text"
                name="countryCode"
                placeholder="+1"
                value={formValues.countryCode}
                onChange={handleInputChange}
                style={{ maxWidth: "80px" }}
                className="custom-placeholder"
              />
              <Form.Control
                type="text"
                name="phone"
                placeholder="123-456-7890"
                value={formValues.phone}
                onChange={handleInputChange}
                className="custom-placeholder"
              />
            </div>
          </Form.Group>
          <div style={{ textAlign: "center" }}>
            <Button variant="dark" type="submit" className="create-button">
              {loading ? "Creating..." : "Create User"}
            </Button>
          </div>
        </Form>
        {statusMessage && (
          <div className="message-container">
            <Alert
              variant={isError ? "danger" : "success"}
              className="status-message"
            >
              {statusMessage}
            </Alert>
          </div>
        )}
      </div>
      {usersLoading ? (
        <div className="loading-container">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <div className="existing-grid">
          <h1 className="user-heading">Existing users</h1>
          {error ? (
            <div className="message-container">
              <Alert variant="danger">{error}</Alert>
            </div>
          ) : (
            <CustomDataGrid
              rows={existingUsersRows}
              columns={existingUsersColumns}
              totalRows={usersLength}
            />
          )}
        </div>
      )}
    </div>
  ) : (
    <div style={{ textAlign: "center", marginTop: "100px" }}>
      <h1>404 - Page Not Found</h1>
      <br />
      <p>The page you're looking for doesn't exist .</p>
    </div>
  );
}

export default CreateNewUser;
